import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { useSuporte, useSuportes } from '../hooks/useSuporte'
import { Suporte, SuporteFilters, SuporteFormData } from '../tipos/suporte'
import { SuporteFiltersComponent } from '../componentes/FiltrosSuporte'
import { SuporteList } from '../componentes/ListaSuporte'
import { FormularioSuporte } from '../componentes/FormularioSuporte'

const convertSuporteToFormData = (suporte: Suporte | null): SuporteFormData | undefined => {
  if (!suporte) return undefined

  return {
    id: suporte.id,
    numeroTicket: suporte.numeroTicket || '',
    nome: suporte.nome || '',
    prefClockify: suporte.prefClockify || '',
    ticketUrl: suporte.ticketUrl || '',
    idProjetoClockify: suporte.idProjetoClockify,
    solicitanteId: suporte.solicitanteId,
    solicitante: suporte.solicitante || '',
    dataAbertura: suporte.dataAbertura || '',
    dataRecebimento: suporte.dataRecebimento || '',
    dataConclusao: suporte.dataConclusao || '',
    statusCodigo: suporte.statusCodigo || '',
    prioridadeCodigo: suporte.prioridadeCodigo || '',
    analistaSMNId: suporte.analistaSMNId ? String(suporte.analistaSMNId) : '',
    desenvolvedorId: suporte.desenvolvedorId ? String(suporte.desenvolvedorId) : '',
    solucao: suporte.solucao || '',
  }
}

function Suportes() {
  const [filters, setFilters] = useState<SuporteFilters>({
    search: '',
    statusCodigo: '',
    prioridadeCodigo: '',
  })
  const [showForm, setShowForm] = useState(false)
  const [editingSuporte, setEditingSuporte] = useState<Suporte | null>(null)
  const [editingSuporteId, setEditingSuporteId] = useState<number | null>(null)

  const { data: suportes = [], isLoading, refetch } = useSuportes(filters)
  const { data: suporteCompleto, isLoading: isLoadingSuporte } = useSuporte(editingSuporteId ?? undefined)

  const suporteParaEdicao = useMemo(() => {
    if (suporteCompleto) return suporteCompleto
    return convertSuporteToFormData(editingSuporte)
  }, [suporteCompleto, editingSuporte])

  const handleSearch = (newFilters: SuporteFilters) => {
    setFilters(newFilters)
  }

  const handleCreateSuporte = () => {
    setEditingSuporte(null)
    setEditingSuporteId(null)
    setShowForm(true)
  }

  const handleEditSuporte = (suporte: Suporte) => {
    setEditingSuporte(suporte)
    setEditingSuporteId(suporte.id || null)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingSuporte(null)
    setEditingSuporteId(null)
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suporte</h1>
          <p className="mt-1 text-sm text-gray-600">Gerencie os chamados de suporte</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreateSuporte}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Novo Suporte
          </button>
        </div>
      </div>

      <SuporteFiltersComponent onSearch={handleSearch} />

      <SuporteList suportes={suportes} isLoading={isLoading} onRefresh={refetch} onEditSuporte={handleEditSuporte} />

      <FormularioSuporte isOpen={showForm} onClose={handleCloseForm} suporte={suporteParaEdicao} isEditing={!!editingSuporteId} />

      {isLoadingSuporte && editingSuporteId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="loading-spinner w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-700">Carregando dados do suporte...</span>
          </div>
        </div>
      )}
    </>
  )
}

export default Suportes
